import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAnalyticsCache } from '../contexts/AnalyticsCacheContext';
import { API } from '../utils/api';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

/* Palette */
const COLORS = ['#047857','#67e8f9','#34d399','#fbbf24','#f87171','#FEF08A','#047857','#38bdf8','#fb923c','#4ade80','#e879f9','#94a3b8'];
const DOC_COLORS = {
  'PhD Informative':'#FDE047','PhD Dissertation':'#FCD34D','Research Paper':'#6d28d9',
  'Technical Drawing':'#a855f7','Blueprint':'#9333ea','Patent':'#c026d3',
  'Instructional Manual':'#d946ef','News Article':'#3b82f6','Academic Paper':'#FCD34D',
  'Government':'#ef4444','Wiki':'#f59e0b','Blog Post':'#FDE68A','Forum':'#06b6d4',
  'Video':'#FEF08A','PDF Document':'#dc2626','Webpage':'#6b7280','Unknown':'#475569',
  'Drawing':'#a855f7','Schematic':'#9333ea'
};

/* Animated Counter */
const AnimNum = ({ end, suffix = '', prefix = '' }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!end) { setV(0); return; }
    let s = 0; const step = Math.max(1, end / 40);
    const id = setInterval(() => { s += step; if (s >= end) { setV(end); clearInterval(id); } else setV(Math.floor(s)); }, 16);
    return () => clearInterval(id);
  }, [end]);
  return <span>{prefix}{v.toLocaleString()}{suffix}</span>;
};

/* Metric Card */
const Metric = ({ label, value, color, suffix = '', prefix = '' }) => (
  <div data-testid={`metric-${label.toLowerCase().replace(/\s+/g,'-')}`} style={{
    flex: '1 1 140px', background: 'rgba(255,255,255,0.015)', backdropFilter: 'blur(12px)',
    borderRadius: 14, padding: '20px 16px', border: `1px solid ${color}12`,
    position: 'relative', overflow: 'hidden', transition: 'border-color 0.3s'
  }}>
    <div style={{ position:'absolute', top:-20, right:-20, width:70, height:70, borderRadius:'50%', background:`${color}06`, filter:'blur(18px)' }} />
    <div style={{ fontSize:'0.58rem', color:'#64748B', textTransform:'uppercase', letterSpacing:2, fontWeight:600, marginBottom:6 }}>{label}</div>
    <div style={{ fontSize:'1.8rem', fontWeight:800, color, letterSpacing:'-0.03em', lineHeight:1 }}>
      <AnimNum end={value} suffix={suffix} prefix={prefix} />
    </div>
  </div>
);

/* Chart Card */
const ChartCard = ({ title, children, span = 1 }) => (
  <div style={{
    background: 'rgba(255,255,255,0.015)', backdropFilter: 'blur(12px)',
    borderRadius: 14, padding: '18px 20px', border: '1px solid rgba(255, 255, 255, 0.85)',
    gridColumn: span > 1 ? `span ${span}` : undefined,
    minHeight: 280
  }}>
    <div style={{ fontSize:'0.62rem', color:'#64748B', textTransform:'uppercase', letterSpacing:2, fontWeight:600, marginBottom:14 }}>{title}</div>
    {children}
  </div>
);

/* Custom Tooltip */
const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'rgba(15,15,25,0.95)', border:'1px solid rgba(203, 213, 225, 0.8)', borderRadius:8, padding:'8px 12px', backdropFilter:'blur(8px)' }}>
      <div style={{ color:'#475569', fontSize:'0.65rem', marginBottom:4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || '#047857', fontSize:'0.72rem', fontWeight:600 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </div>
      ))}
    </div>
  );
};

/* Export CSV helper */
const exportCSV = (data) => {
  if (!data) return;
  const rows = [['Metric', 'Value']];
  const s = data.summary || {};
  rows.push(['Total Results', s.total_results]);
  rows.push(['Categorized', s.categorized]);
  rows.push(['Categorization Rate %', s.categorization_rate]);
  rows.push(['Categories', s.total_categories]);
  rows.push(['Task Success Rate %', s.task_success_rate]);
  rows.push(['HC Tagged', s.hot_civilian_tagged]);
  rows.push([]);
  rows.push(['Date', 'Results', 'Avg Score %']);
  (data.activity_timeline || []).forEach(a => rows.push([a.date, a.results, a.avg_score]));
  rows.push([]);
  rows.push(['Document Type', 'Count']);
  (data.document_types || []).forEach(d => rows.push([d.name, d.value]));
  rows.push([]);
  rows.push(['Category', 'Results']);
  (data.top_categories || []).forEach(c => rows.push([c.name, c.results]));
  rows.push([]);
  rows.push(['Domain', 'Count']);
  (data.top_domains || []).forEach(d => rows.push([d.domain, d.count]));

  const csv = rows.map(r => (r.length ? r.join(',') : '')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `search-analytics-${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
};

/* Main Page */
const SearchAnalyticsPage = () => {
  const { user, token } = useAuth();
  const { cache: analyticsCache, updateCache } = useAnalyticsCache();
  const [data, setData] = useState(() => analyticsCache || null);
  const [loading, setLoading] = useState(!analyticsCache);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const mountedRef = useRef(true);

  const loadAnalytics = useCallback((newDays) => {
    const d = newDays || days;
    if (!token) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    
    // Try reading from context cache first
    if (d === 30 && analyticsCache) {
      setData(analyticsCache);
      setLoading(false);
      return;
    }
    
    // Otherwise use XHR (this may not work if the queue is congested,
    // but it's needed for different day ranges and refresh)
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/search-analytics?days=${d}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.timeout = 30000;
    xhr.onload = function() {
      if (!mountedRef.current) return;
      if (xhr.status === 200) {
        try {
          const parsed = JSON.parse(xhr.responseText);
          setData(parsed);
          if (d === 30) updateCache(parsed);
        } catch(e) { setError('Parse error'); }
      } else { setError(`Server error ${xhr.status}`); }
      setLoading(false);
    };
    xhr.onerror = function() { if (mountedRef.current) { setError('Network error - try navigating to Search page first, then back here'); setLoading(false); } };
    xhr.ontimeout = function() { if (mountedRef.current) { setError('Timeout - try navigating to Search page first, then back here'); setLoading(false); } };
    xhr.send();
  }, [token, days, analyticsCache, updateCache]);

  // Load on mount - check cache first
  useEffect(() => {
    mountedRef.current = true;
    if (analyticsCache) {
      setData(analyticsCache);
      setLoading(false);
    } else if (token) {
      // If no cache, try loading (may work if queue is clear)
      loadAnalytics();
    } else {
      setLoading(false);
    }
    return () => { mountedRef.current = false; };
  }, []);

  const handleDaysChange = (d) => {
    setDays(d);
    setCustomStart('');
    setCustomEnd('');
    loadAnalytics(d);
  };

  // Apply custom date range filter to cached data
  const filteredData = React.useMemo(() => {
    if (!data || !customStart || !customEnd) return data;
    const start = new Date(customStart);
    const end = new Date(customEnd);
    end.setHours(23, 59, 59);
    
    const filtered = { ...data };
    if (data.activity_timeline) {
      filtered.activity_timeline = data.activity_timeline.filter(item => {
        const d = new Date(item.date);
        return d >= start && d <= end;
      });
    }
    return filtered;
  }, [data, customStart, customEnd]);

  if (!user) return <div style={{ padding:40, textAlign:'center', color:'rgba(255,255,255,0.4)' }}>Please log in to view analytics.</div>;

  const s = data?.summary || {};
  const eiTagged = s.educated_individual_tagged || s.hot_civilian_tagged || 0;
  const displayData = filteredData || data;

  return (
    <div data-testid="search-analytics-page" style={{ maxWidth:1100, margin:'0 auto', padding:'24px 20px' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ color:'rgba(255,255,255,0.9)', fontSize:'1.6rem', fontWeight:800, letterSpacing:'-0.03em', margin:0 }}>Search Analytics</h1>
          <p style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.75rem', marginTop:3, margin:0 }}>Insights into your search patterns and categorization performance</p>
        </div>
        <div style={{ display:'flex', gap:4, alignItems:'center', flexWrap:'wrap' }}>
          {[7,30,90].map(d => (
            <button key={d} onClick={() => handleDaysChange(d)} data-testid={`days-${d}`} style={{
              background: days === d && !customStart ? 'rgba(192,132,252,0.15)' : 'transparent',
              border: `1px solid ${days === d && !customStart ? 'rgba(192,132,252,0.3)' : 'rgba(255,255,255,0.06)'}`,
              color: days === d && !customStart ? '#047857' : '#64748B',
              padding:'5px 14px', borderRadius:8, fontSize:'0.7rem', fontWeight:600, cursor:'pointer'
            }}>{d}d</button>
          ))}
          <div style={{ display:'flex', gap:4, alignItems:'center' }}>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              data-testid="custom-date-start"
              style={{
                background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(203, 213, 225, 0.8)',
                color: '#334155', padding: '4px 8px', borderRadius: 6,
                fontSize: '0.65rem', outline: 'none'
              }}
            />
            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem' }}>to</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              data-testid="custom-date-end"
              style={{
                background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(203, 213, 225, 0.8)',
                color: '#334155', padding: '4px 8px', borderRadius: 6,
                fontSize: '0.65rem', outline: 'none'
              }}
            />
            {customStart && customEnd && (
              <button
                onClick={() => { setCustomStart(''); setCustomEnd(''); }}
                data-testid="clear-date-range"
                style={{
                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                  color: '#f87171', padding: '4px 10px', borderRadius: 6, fontSize: '0.65rem',
                  fontWeight: 600, cursor: 'pointer'
                }}
              >Clear</button>
            )}
          </div>
          <button onClick={() => exportCSV(data)} data-testid="export-csv" disabled={!data} style={{
            background: data ? 'rgba(52,211,153,0.1)' : 'rgba(255, 255, 255, 0.85)',
            border: `1px solid ${data ? 'rgba(52,211,153,0.2)' : 'rgba(255,255,255,0.06)'}`,
            color: data ? '#34d399' : 'rgba(255,255,255,0.2)',
            padding:'5px 14px', borderRadius:8, fontSize:'0.7rem', fontWeight:600, cursor: data ? 'pointer' : 'default'
          }}>Export CSV</button>
          <button onClick={() => loadAnalytics()} data-testid="refresh-analytics" style={{
            background: 'rgba(192,132,252,0.1)', border: '1px solid rgba(192,132,252,0.2)',
            color: '#047857', padding:'5px 14px', borderRadius:8, fontSize:'0.7rem', fontWeight:600, cursor:'pointer'
          }}>Refresh</button>
        </div>
      </div>

      {loading ? (
        <div data-testid="analytics-loading" style={{ padding:60, textAlign:'center' }}>
          <div style={{ width:28, height:28, border:'2px solid rgba(255,255,255,0.1)', borderTopColor:'#047857', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto' }} />
          <p style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.75rem', marginTop:12 }}>Loading analytics data...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : error ? (
        <div data-testid="analytics-error" style={{ padding:60, textAlign:'center' }}>
          <p style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.85rem' }}>{error}</p>
          <button onClick={() => loadAnalytics()} data-testid="retry-analytics" style={{
            marginTop:8, background:'rgba(192,132,252,0.1)', border:'1px solid rgba(192,132,252,0.2)',
            color:'#047857', padding:'8px 20px', borderRadius:8, fontSize:'0.75rem', fontWeight:600, cursor:'pointer'
          }}>Retry</button>
        </div>
      ) : !data ? (
        <div style={{ padding:60, textAlign:'center' }}>
          <p style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.85rem' }}>No analytics data available</p>
          <button onClick={() => loadAnalytics()} data-testid="retry-analytics" style={{
            marginTop:8, background:'rgba(192,132,252,0.1)', border:'1px solid rgba(192,132,252,0.2)',
            color:'#047857', padding:'8px 20px', borderRadius:8, fontSize:'0.75rem', fontWeight:600, cursor:'pointer'
          }}>Load Data</button>
        </div>
      ) : (
        <>
          {/* KPI Metrics Row */}
          <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
            <Metric label="Total Results" value={s.total_results} color="#047857" />
            <Metric label="Categorized" value={s.categorized} color="#34d399" />
            <Metric label="Cat. Rate" value={s.categorization_rate} color="#67e8f9" suffix="%" />
            <Metric label="Categories" value={s.total_categories} color="#fbbf24" />
            <Metric label="Task Success" value={s.task_success_rate} color="#34d399" suffix="%" />
            <Metric label="EI Tagged" value={eiTagged} color="#a855f7" />
          </div>

          {/* Charts Grid */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap:14 }}>

            {/* Activity Timeline */}
            <ChartCard title="Search Activity Over Time" span={2}>
              {displayData?.activity_timeline?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={displayData.activity_timeline} margin={{ top:5, right:10, left:0, bottom:0 }}>
                    <defs>
                      <linearGradient id="gResults" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#047857" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#047857" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gScore" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
                    <XAxis dataKey="date" tick={{ fill:'rgba(255,255,255,0.25)', fontSize:10 }} tickLine={false} axisLine={{ stroke:'rgba(255,255,255,0.06)' }} />
                    <YAxis tick={{ fill:'rgba(255,255,255,0.25)', fontSize:10 }} tickLine={false} axisLine={false} />
                    <Tooltip content={<DarkTooltip />} />
                    <Legend wrapperStyle={{ fontSize:'0.65rem', color:'rgba(255,255,255,0.4)' }} />
                    <Area type="monotone" dataKey="results" name="Results" stroke="#047857" fill="url(#gResults)" strokeWidth={2} />
                    <Area type="monotone" dataKey="avg_score" name="Avg Score %" stroke="#34d399" fill="url(#gScore)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.2)', fontSize:'0.8rem' }}>No activity data yet - run some searches!</div>
              )}
            </ChartCard>

            {/* Document Type Distribution */}
            <ChartCard title="Document Types">
              {displayData?.document_types?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={displayData.document_types.slice(0, 10)}
                      cx="50%" cy="50%"
                      innerRadius={50} outerRadius={85}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {displayData.document_types.slice(0, 10).map((entry, i) => (
                        <Cell key={i} fill={DOC_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<DarkTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.2)', fontSize:'0.8rem' }}>No document data</div>
              )}
              {/* Legend */}
              <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
                {(displayData?.document_types || []).slice(0, 8).map((dt, i) => (
                  <span key={i} style={{ display:'flex', alignItems:'center', gap:3, fontSize:'0.58rem', color:'rgba(255,255,255,0.4)' }}>
                    <span style={{ width:6, height:6, borderRadius:2, background: DOC_COLORS[dt.name] || COLORS[i % COLORS.length] }} />
                    {dt.name} ({dt.value})
                  </span>
                ))}
              </div>
            </ChartCard>

            {/* Top Categories */}
            <ChartCard title="Top Categories by Results">
              {displayData?.top_categories?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={displayData.top_categories.slice(0, 8)} layout="vertical" margin={{ left:10, right:10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" horizontal={false} />
                    <XAxis type="number" tick={{ fill:'rgba(255,255,255,0.25)', fontSize:10 }} axisLine={false} tickLine={false} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fill:'rgba(255,255,255,0.4)', fontSize:10 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<DarkTooltip />} />
                    <Bar dataKey="results" radius={[0, 4, 4, 0]} maxBarSize={18}>
                      {displayData.top_categories.slice(0, 8).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.2)', fontSize:'0.8rem' }}>No category data</div>
              )}
            </ChartCard>

            {/* Top Domains */}
            <ChartCard title="Top Domains">
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {(displayData?.top_domains || []).slice(0, 8).map((d, i) => {
                  const maxCount = displayData?.top_domains?.[0]?.count || 1;
                  const pct = (d.count / maxCount) * 100;
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ color:'#475569', fontSize:'0.68rem', minWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.domain}</span>
                      <div style={{ flex:1, height:6, borderRadius:3, background:'rgba(255, 255, 255, 0.85)', overflow:'hidden' }}>
                        <div style={{ width:`${pct}%`, height:'100%', borderRadius:3, background: COLORS[i % COLORS.length], transition:'width 0.6s ease' }} />
                      </div>
                      <span style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.62rem', minWidth:24, textAlign:'right' }}>{d.count}</span>
                    </div>
                  );
                })}
                {(!displayData?.top_domains || displayData.top_domains.length === 0) && (
                  <div style={{ padding:40, textAlign:'center', color:'rgba(255,255,255,0.2)', fontSize:'0.8rem' }}>No domain data</div>
                )}
              </div>
            </ChartCard>

            {/* Search Depth Distribution */}
            <ChartCard title="Search Depth Usage">
              {displayData?.depth_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={displayData.depth_distribution} margin={{ top:5, right:10, left:0, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
                    <XAxis dataKey="depth" tick={{ fill:'rgba(255,255,255,0.25)', fontSize:10 }} axisLine={{ stroke:'rgba(255,255,255,0.06)' }} />
                    <YAxis tick={{ fill:'rgba(255,255,255,0.25)', fontSize:10 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<DarkTooltip />} />
                    <Bar dataKey="tasks" name="Tasks" radius={[4, 4, 0, 0]} maxBarSize={32}>
                      {displayData.depth_distribution.map((entry, i) => {
                        const d = entry.depth || 5;
                        const color = d <= 3 ? '#94a3b8' : d <= 6 ? '#34d399' : d <= 8 ? '#fbbf24' : '#ef4444';
                        return <Cell key={i} fill={color} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.2)', fontSize:'0.8rem' }}>No depth data yet</div>
              )}
            </ChartCard>

          </div>
        </>
      )}
    </div>
  );
};

export default SearchAnalyticsPage;
